"use client";

import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsHydrated } from "@/hooks/useIsHydrated";

type HeaderLocationDisplayProps = {
  /** Applied to resolved location text (not loading placeholders). */
  className?: string;
  /** Applied while auth or location is still resolving after hydration. */
  loadingClassName?: string;
  /** Optional ", Lagos" suffix for callers that want the city spelled out. */
  includeLagosSuffix?: boolean;
  /** Quiet prefix such as "Near ". Omitted while the label is still loading. */
  prefix?: string;
};

/**
 * Location label for headers. Defers client-only location/auth values until
 * after hydration so SSR markup matches the first client render.
 */
export default function HeaderLocationDisplay({
  className = "",
  loadingClassName = "text-text-light",
  includeLagosSuffix = false,
  prefix = "",
}: HeaderLocationDisplayProps) {
  const isHydrated = useIsHydrated();
  const { userLocation, isLoadingLocation } = useLocation();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  if (!isHydrated || isAuthLoading) {
    return <span className={loadingClassName}>Loading...</span>;
  }

  if (isAuthenticated && user?.location) {
    return (
      <span className={className}>
        {prefix}
        {user.location.split(",")[0]}
      </span>
    );
  }

  if (isLoadingLocation) {
    return <span className={loadingClassName}>Loading location...</span>;
  }

  if (userLocation) {
    const area = includeLagosSuffix
      ? `${userLocation.areaName}, Lagos`
      : userLocation.areaName;
    return (
      <span className={className}>
        {prefix}
        {area}
      </span>
    );
  }

  return <span className={className}>{prefix}Lagos, Nigeria</span>;
}
